import axios from "axios";
import {
  type ChangeEvent,
  type FormEvent,
  useEffect,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { IconArrowLeft } from "../components/BenchmarkIcons";
import {
  fetchPublisherAppUpdateContext,
  submitApplicationVersionUpdate,
  type PublisherAppUpdateContext,
} from "../services/apps";
import {
  collectVersionUpdateValidationErrors,
  validateApkFileOnSelect,
} from "../services/publishAppValidation";
import { PUBLISH_APK_ACCEPT } from "../services/publishAppFileSelection";

export function UpdateAppPage() {
  const navigate = useNavigate();
  const { applicationId = "" } = useParams();
  const [app, setApp] = useState<PublisherAppUpdateContext | null>(null);
  const [newVersion, setNewVersion] = useState("");
  const [apkFile, setApkFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [apkFieldError, setApkFieldError] = useState<string | undefined>();

  useEffect(() => {
    if (!applicationId) {
      setFormError("Application not found.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setFormError("");

    void fetchPublisherAppUpdateContext(applicationId)
      .then((result) => {
        setApp(result.app);
      })
      .catch(() => {
        setApp(null);
        setFormError("Unable to load this application for update.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [applicationId]);

  const handleVersionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNewVersion(event.target.value);
  };

  const handleApkChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const selected = input.files?.[0] ?? null;
    if (!selected) {
      setApkFile(null);
      setApkFieldError(undefined);
      return;
    }

    const error = validateApkFileOnSelect(selected);
    if (error) {
      setApkFile(null);
      setApkFieldError(error);
      input.value = "";
      return;
    }

    setApkFile(selected);
    setApkFieldError(undefined);
  };

  function collectValidationErrors(): string[] {
    return collectVersionUpdateValidationErrors({
      newVersion,
      currentVersion: app?.current_version ?? null,
      apkFile,
    });
  }

  const isFormValid = collectValidationErrors().length === 0 && !apkFieldError;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) {
      return;
    }

    setFormError("");
    setFormSuccess("");

    const errors = collectValidationErrors();
    setValidationErrors(errors);
    if (errors.length > 0 || !apkFile || !applicationId) {
      return;
    }

    const formData = new FormData();
    formData.append("version", newVersion.trim());
    formData.append("apk", apkFile);

    setSubmitting(true);

    try {
      const result = await submitApplicationVersionUpdate(applicationId, formData);
      setFormSuccess(result.message);

      if (result.activated) {
        navigate("/publisher-dashboard", { replace: true });
      }
    } catch (error: unknown) {
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? String(error.response.data.message)
          : "Version update failed. Please review your details and try again.";
      const apiErrors = axios.isAxiosError(error)
        ? (error.response?.data?.errors as string[] | undefined)
        : undefined;
      setFormError(message);
      setValidationErrors(apiErrors ?? []);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <main className="oac-publish-scale-frame oac-publish-scale-frame--update">
        <div className="oac-publish oac-publish--update">
          <p className="oac-form-message">Loading application...</p>
        </div>
      </main>
    );
  }

  if (!app) {
    return (
      <main className="oac-publish-scale-frame oac-publish-scale-frame--update">
        <div className="oac-publish oac-publish--update">
          <p className="oac-form-message oac-form-message--error">
            {formError || "Application not found."}
          </p>
          <p className="oac-update-app__back oac-update-app__back--standalone">
            <Link
              aria-label="Back to Publisher Dashboard"
              className="oac-installation-steps-page__back-link"
              title="Back to Publisher Dashboard"
              to="/publisher-dashboard"
            >
              <IconArrowLeft
                className="oac-installation-steps-page__back-icon"
                size={40}
              />
            </Link>
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="oac-publish-scale-frame oac-publish-scale-frame--update">
      <div className="oac-publish oac-publish--update">
        <header className="oac-publish__header">
          <h1>Update App</h1>
          <p>Upload a new APK version for {app.name}.</p>
        </header>

        <form className="oac-publish__form" onSubmit={handleSubmit}>
          <section className="oac-publish__left">
            <div className="oac-publish__field">
              <label htmlFor="app-name">App name</label>
              <input
                disabled
                id="app-name"
                readOnly
                type="text"
                value={app.name}
              />
            </div>

            <div className="oac-publish__field">
              <label htmlFor="current-version">Current version</label>
              <input
                disabled
                id="current-version"
                readOnly
                type="text"
                value={app.current_version ?? "—"}
              />
            </div>

            <div className="oac-publish__field">
              <label htmlFor="new-version">New version</label>
              <input
                id="new-version"
                name="newVersion"
                onChange={handleVersionChange}
                placeholder="e.g. 1.1.0"
                required
                type="text"
                value={newVersion}
              />
            </div>

            <div className="oac-publish__field">
              <label htmlFor="apk-file">New APK file</label>
              <label className="oac-publish__apk-upload" htmlFor="apk-file">
                <input
                  accept={PUBLISH_APK_ACCEPT}
                  aria-label="Upload APK file"
                  className="oac-publish__apk-upload-input"
                  id="apk-file"
                  onChange={handleApkChange}
                  required
                  type="file"
                />
                <span
                  aria-hidden="true"
                  className="oac-upload__symbol oac-publish__apk-upload-icon"
                >
                  <span className="oac-upload__arrow">↑</span>
                  <span className="oac-upload__tray" />
                </span>
                {apkFile ? (
                  <span className="oac-publish__apk-filename">{apkFile.name}</span>
                ) : null}
              </label>
              {apkFieldError && (
                <p className="oac-form-message oac-form-message--error">{apkFieldError}</p>
              )}
            </div>

            <p className="oac-update-app__back">
              <Link
                aria-label="Back to Publisher Dashboard"
                className="oac-installation-steps-page__back-link"
                title="Back to Publisher Dashboard"
                to="/publisher-dashboard"
              >
                <IconArrowLeft
                  className="oac-installation-steps-page__back-icon"
                  size={40}
                />
              </Link>
            </p>
          </section>

          <section className="oac-publish__right">
            {(validationErrors.length > 0 || formError || formSuccess) && (
              <div className="oac-publish__validation">
                {formSuccess && (
                  <p className="oac-form-message">{formSuccess}</p>
                )}
                {formError && (
                  <p className="oac-form-message oac-form-message--error">{formError}</p>
                )}
                {validationErrors.map((error) => (
                  <p className="oac-form-message oac-form-message--error" key={error}>
                    {error}
                  </p>
                ))}
              </div>
            )}

            <button
              className={[
                "oac-publish__button",
                "publish-btn",
                isFormValid ? "enabled" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              disabled={!isFormValid || submitting}
              type="submit"
            >
              {submitting ? "Uploading..." : "Upload New Version"}
            </button>
          </section>
        </form>
      </div>
    </main>
  );
}
